import { FormValueModel, NormalizeFormValuesForDatabaseService } from '@domain/dynamic-form';
import { QueryParamsProvider } from '@domain/routing';
import {
	ColumnModel,
	DataModel,
	fileBooleanMappings,
	fileBooleanToString,
	FormActionTypeEnum,
	PrimaryKeyValueModel,
	TableTypeEnum
} from '@shared';
import { WorkspaceDatabaseKeys } from '@database/workspace';

export class FormNormalizerBuilder {
	private model: FormValueModel = {};

	public constructor(
		formInitialValue: FormValueModel,
		private queryParamsProvider: QueryParamsProvider,
		private normalizeFormValuesForDatabaseService: NormalizeFormValuesForDatabaseService
	) {
		this.model = { ...formInitialValue };
	}

	public setBaseProperties(projectNumber: string): FormNormalizerBuilder {
		this.model[WorkspaceDatabaseKeys.columns.projectNumber] = projectNumber;
		this.model[WorkspaceDatabaseKeys.internalColumns.isDeleted] = false;

		if (this.queryParamsProvider.formActionType() !== FormActionTypeEnum.edit) {
			this.model[WorkspaceDatabaseKeys.columns.externalCreatedRecord] = fileBooleanToString(true);
		}

		this.model[WorkspaceDatabaseKeys.columns.externalModifiedRecord] = fileBooleanToString(true);
		return this;
	}

	public setPropertiesBasedOnDataType(dataColumns: ColumnModel[]): FormNormalizerBuilder {
		const dataVisibleColumns = dataColumns.filter(
			(dataColumn: ColumnModel) => fileBooleanMappings[dataColumn[WorkspaceDatabaseKeys.columns.displayColumn]]
		);

		this.model = this.normalizeFormValuesForDatabaseService.prepareValuesBasedOnDataType(
			dataVisibleColumns,
			this.model
		);

		return this;
	}

	public setSampleParentLink(
		tableTypes: string[],
		currentParentSample: DataModel,
		formActionType: FormActionTypeEnum
	): FormNormalizerBuilder {
		if (tableTypes.includes(TableTypeEnum.sampleTable) || tableTypes.includes(TableTypeEnum.flatSampleTable)) {
			if (formActionType === FormActionTypeEnum.duplicate) {
				this.model[WorkspaceDatabaseKeys.columns.parentSampleNumber] =
					currentParentSample[WorkspaceDatabaseKeys.columns.parentSampleNumber];
			} else {
				this.model[WorkspaceDatabaseKeys.columns.parentSampleNumber] = this.model[
					WorkspaceDatabaseKeys.columns.sampleNumber
				]
					.toString()
					.trim();
			}
		}

		return this;
	}

	public setPrimaryKey(tableTypes: string[], primaryKeyAndValue: PrimaryKeyValueModel[]): FormNormalizerBuilder {
		if (
			!tableTypes.includes(TableTypeEnum.childTable) ||
			tableTypes.includes(TableTypeEnum.flatSampleTable) ||
			tableTypes.includes(TableTypeEnum.standardTable)
		) {
			primaryKeyAndValue.forEach(
				/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
				({ primaryKey, primaryKeyValue }) => (this.model[primaryKey] = this.model[primaryKey].toString().trim())
			);
		} else {
			primaryKeyAndValue.forEach(({ primaryKey, primaryKeyValue }) => (this.model[primaryKey] = primaryKeyValue));
		}

		return this;
	}

	public toStringSampleAndStadardProperties(): FormNormalizerBuilder {
		if (this.model.hasOwnProperty(WorkspaceDatabaseKeys.columns.sampleNumber)) {
			this.model[WorkspaceDatabaseKeys.columns.sampleNumber] = this.model[
				WorkspaceDatabaseKeys.columns.sampleNumber
			]
				.toString()
				.trim();
		}

		if (this.model.hasOwnProperty(WorkspaceDatabaseKeys.columns.standardNumber)) {
			this.model[WorkspaceDatabaseKeys.columns.standardNumber] = this.model[
				WorkspaceDatabaseKeys.columns.standardNumber
			]
				.toString()
				.trim();
		}

		return this;
	}

	public build(): FormValueModel {
		return this.model;
	}
}
